import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtINRCompact } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import VaultBriefV2 from "./VaultBriefV2";
import FinancialPositionHero from "./FinancialPositionHero";

/**
 * Home Hero Card · v2
 *
 * The signature "wow" area of VAULT Home. Composes:
 *   1. VAULT Brief (intelligent summary with time-window chips)
 *   2. Financial Position Hero (net position + assets/liabilities bars)
 *   3. Cash Flow summary strip (In · Out · Savings · Net)
 *
 * Pulls from endpoints that Home.js already uses. Zero new backend.
 * All requests are non-blocking — the hero still renders with `—`
 * placeholders while data is in-flight.
 */
export default function HomeHeroCard() {
    const { user } = useAuth();
    const [nw, setNw]         = useState(null);
    const [fr, setFr]         = useState(null);
    const [flow, setFlow]     = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        async function load() {
            setLoading(true);
            try {
                const [nwRes, frRes, flowRes] = await Promise.allSettled([
                    api.get("/net-worth"),
                    api.get("/finance-review"),
                    api.get("/cash-flow-sankey?preset=this_month"),
                ]);
                if (!alive) return;
                if (nwRes.status === "fulfilled")   setNw(nwRes.value.data);
                if (frRes.status === "fulfilled")   setFr(frRes.value.data);
                if (flowRes.status === "fulfilled") setFlow(flowRes.value.data);
            } finally {
                if (alive) setLoading(false);
            }
        }
        load();
        return () => { alive = false; };
    }, []);

    const assets      = nw?.total_assets ?? nw?.assets ?? 0;
    const liabilities = nw?.total_liabilities ?? nw?.liabilities ?? 0;

    const counts = {
        payments_due:    (fr?.counts?.payments_due ?? fr?.payments_due?.length ?? 0),
        subs_renewing:   (fr?.counts?.subs_renewing ?? 0),
        docs_attention:  (fr?.counts?.missing_statements ?? 0),
        overdue:         (fr?.counts?.overdue_cards ?? fr?.overdue_cards?.length ?? 0),
    };

    const flowIn   = flow?.kpis?.total_income ?? 0;
    const flowOut  = flow?.kpis?.total_expenses ?? 0;
    const flowSave = flow?.kpis?.net_surplus ?? (flowIn - flowOut);
    const savePct  = flow?.kpis?.savings_rate ?? (flowIn ? Math.round((flowSave / flowIn) * 100) : 0);

    return (
        <section className="v2-scope mb-8" data-testid="v2-home-hero">
            <div className="v2-hero-grid">
                <VaultBriefV2
                    greetingName={user?.name}
                    counts={counts}
                    window="week"
                />
                <FinancialPositionHero
                    assets={assets}
                    liabilities={liabilities}
                    loading={loading}
                />
            </div>

            {/* Cash Flow summary strip — surfaces this-month direction at a glance */}
            <div className="v2-hero mt-6" data-testid="v2-flow-strip">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <span className="v2-eyebrow" style={{ color: "var(--v2-ink-inv-soft)" }}>
                            Money Flow · This Month
                        </span>
                        <div className="v2-greeting__lede mt-1">
                            Where your money went, and what stayed.
                        </div>
                    </div>
                    <Link
                        to="/reports/cash-flow"
                        className="v2-btn v2-btn--ghost"
                        data-testid="v2-flow-open-money-map"
                    >
                        Open Money Map <ArrowRight size={12} />
                    </Link>
                </div>

                <div className="v2-flow">
                    <div className="v2-flow__tile" data-testid="v2-flow-in">
                        <span className="v2-flow__label">In</span>
                        <span className="v2-flow__value v2-flow__value--in">
                            {fmtINRCompact(flowIn)}
                        </span>
                    </div>
                    <div className="v2-flow__tile" data-testid="v2-flow-out">
                        <span className="v2-flow__label">Out</span>
                        <span className="v2-flow__value v2-flow__value--out">
                            {fmtINRCompact(flowOut)}
                        </span>
                    </div>
                    <div className="v2-flow__tile" data-testid="v2-flow-save">
                        <span className="v2-flow__label">Savings</span>
                        <span className="v2-flow__value v2-flow__value--save">
                            {fmtINRCompact(Math.max(0, flowSave))}
                        </span>
                    </div>
                    <div className="v2-flow__tile" data-testid="v2-flow-rate">
                        <span className="v2-flow__label">Rate</span>
                        <span className="v2-flow__value v2-flow__value--net">
                            {savePct}<span style={{ fontSize: 14, opacity: 0.7 }}>%</span>
                        </span>
                    </div>
                </div>
            </div>
        </section>
    );
}
